import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Calendar, Upload, FileSpreadsheet, CheckCircle2, AlertCircle,
    Clock, AlertTriangle, Plane, FileCheck, Trash2, DollarSign,
    Users, TrendingUp, TrendingDown, Award, Download
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Nomina = () => {
    const [tab, setTab] = useState('subir');
    const [empleados, setEmpleados] = useState([]);
    const [periodos, setPeriodos] = useState([]);
    const [vacaciones, setVacaciones] = useState([]);
    const [justificaciones, setJustificaciones] = useState([]);

    const [previewData, setPreviewData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    // Vacaciones modal
    const [vacOpen, setVacOpen] = useState(false);
    const [vacForm, setVacForm] = useState({ empleado_id: '', fecha_inicio: '', fecha_fin: '', motivo: '' });

    // Justificaciones modal
    const [justOpen, setJustOpen] = useState(false);
    const [justForm, setJustForm] = useState({ empleado_id: '', fecha: '', tipo: 'falta', motivo: '' });
    const [justGlobal, setJustGlobal] = useState(false);
    const [justExcepciones, setJustExcepciones] = useState([]); // ids de empleados a excluir cuando es global

    // Detalle periodo
    const [detalleOpen, setDetalleOpen] = useState(false);
    const [periodoDetalle, setPeriodoDetalle] = useState(null);

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        try {
            const [eRes, pRes, vRes, jRes] = await Promise.all([
                axios.get(`${API_URL}/api/empleados`),
                axios.get(`${API_URL}/api/nomina/periodos`),
                axios.get(`${API_URL}/api/vacaciones`),
                axios.get(`${API_URL}/api/justificaciones`)
            ]);
            setEmpleados(eRes.data.filter(e => e.estatus === 'activo'));
            setPeriodos(pRes.data);
            setVacaciones(vRes.data);
            setJustificaciones(jRes.data);
        } catch (e) {
            toast.error('Error al cargar datos');
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setPreviewData(null);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await axios.post(`${API_URL}/api/nomina/preview`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setPreviewData(res.data);
            toast.success(`Excel procesado: ${res.data.vinculados.length} vinculados, ${res.data.no_vinculados.length} no vinculados`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error procesando Excel');
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const handleAprobar = async () => {
        if (!previewData) return;
        if (!window.confirm(`Aprobar nomina de ${previewData.fecha_inicio} a ${previewData.fecha_fin}?\nTotal a pagar: $${previewData.vinculados.reduce((s, r) => s + r.pago_neto, 0).toFixed(2)}`)) return;
        try {
            await axios.post(`${API_URL}/api/nomina/aprobar`, {
                fecha_inicio: previewData.fecha_inicio,
                fecha_fin: previewData.fecha_fin,
                registros: previewData.vinculados,
                observaciones: ''
            });
            toast.success('Nomina aprobada y guardada');
            setPreviewData(null);
            fetchAll();
            setTab('periodos');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al aprobar');
        }
    };

    const handleVacSubmit = async (e) => {
        e.preventDefault();
        if (!vacForm.empleado_id || !vacForm.fecha_inicio || !vacForm.fecha_fin) {
            toast.error('Complete todos los campos'); return;
        }
        try {
            await axios.post(`${API_URL}/api/vacaciones`, vacForm);
            toast.success('Vacacion registrada');
            setVacOpen(false);
            setVacForm({ empleado_id: '', fecha_inicio: '', fecha_fin: '', motivo: '' });
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    };

    const handleVacDelete = async (id) => {
        if (!window.confirm('Eliminar esta vacacion?')) return;
        try { await axios.delete(`${API_URL}/api/vacaciones/${id}`); toast.success('Eliminada'); fetchAll(); }
        catch (err) { toast.error('Error'); }
    };

    const handleJustSubmit = async (e) => {
        e.preventDefault();
        if (!justForm.fecha || !justForm.motivo) {
            toast.error('Complete fecha y motivo'); return;
        }
        if (!justGlobal && !justForm.empleado_id) {
            toast.error('Seleccione un empleado o active el modo global'); return;
        }
        try {
            if (justGlobal) {
                // Crear una justificacion por cada empleado activo (excepto los excluidos)
                const empleadosObjetivo = empleados.filter(emp => !justExcepciones.includes(emp.id) && (emp.activo !== false));
                if (empleadosObjetivo.length === 0) {
                    toast.error('No hay empleados a justificar'); return;
                }
                let ok = 0, fail = 0;
                for (const emp of empleadosObjetivo) {
                    try {
                        await axios.post(`${API_URL}/api/justificaciones`, {
                            empleado_id: emp.id,
                            fecha: justForm.fecha,
                            tipo: justForm.tipo,
                            motivo: justForm.motivo
                        });
                        ok++;
                    } catch (err) { fail++; }
                }
                toast.success(`${ok} justificacion${ok !== 1 ? 'es' : ''} creada${ok !== 1 ? 's' : ''}${fail ? ` (${fail} fallaron)` : ''}`);
            } else {
                await axios.post(`${API_URL}/api/justificaciones`, justForm);
                toast.success('Justificacion registrada');
            }
            setJustOpen(false);
            setJustForm({ empleado_id: '', fecha: '', tipo: 'falta', motivo: '' });
            setJustGlobal(false);
            setJustExcepciones([]);
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    };

    const handleJustDelete = async (id) => {
        if (!window.confirm('Eliminar esta justificacion?')) return;
        try { await axios.delete(`${API_URL}/api/justificaciones/${id}`); toast.success('Eliminada'); fetchAll(); }
        catch (err) { toast.error('Error'); }
    };

    const verPeriodoDetalle = async (id) => {
        try {
            const res = await axios.get(`${API_URL}/api/nomina/periodos/${id}`);
            setPeriodoDetalle(res.data);
            setDetalleOpen(true);
        } catch (err) { toast.error('Error'); }
    };

    const descargarRecibo = async (periodoId, empleadoId, empleadoNombre) => {
        try {
            const res = await axios.get(`${API_URL}/api/nomina/periodos/${periodoId}/recibo/${empleadoId}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `recibo_${empleadoNombre.replace(/\s/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error('Error al descargar recibo');
        }
    };

    const totalPagar = previewData ? previewData.vinculados.reduce((s, r) => s + r.pago_neto, 0) : 0;
    const totalDesc = previewData ? previewData.vinculados.reduce((s, r) => s + r.desc_total, 0) : 0;
    const totalBonos = previewData ? previewData.vinculados.reduce((s, r) => s + r.monto_bono, 0) : 0;

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="nomina-page">
            <div>
                <h1 className="text-2xl font-bold text-zinc-100">Nomina / Asistencia Biometrica</h1>
                <p className="text-zinc-400 mt-1">Sube el Excel del reloj y calcula descuentos, bonos y pagos automaticamente.</p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="subir" data-testid="tab-subir"><Upload className="w-4 h-4 mr-2" /> Subir Excel</TabsTrigger>
                    <TabsTrigger value="periodos" data-testid="tab-periodos"><FileCheck className="w-4 h-4 mr-2" /> Periodos</TabsTrigger>
                    <TabsTrigger value="vacaciones" data-testid="tab-vacaciones"><Plane className="w-4 h-4 mr-2" /> Vacaciones</TabsTrigger>
                    <TabsTrigger value="justificaciones" data-testid="tab-justificaciones"><AlertTriangle className="w-4 h-4 mr-2" /> Justificaciones</TabsTrigger>
                </TabsList>

                {/* SUBIR EXCEL */}
                <TabsContent value="subir" className="space-y-4 mt-4">
                    <Card className="card">
                        <CardContent className="p-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <FileSpreadsheet className="w-12 h-12 text-amber-400" />
                                <div className="flex-1">
                                    <p className="font-semibold text-zinc-100">Subir archivo del reloj biometrico</p>
                                    <p className="text-sm text-zinc-400">Formato: .xlsx o .xls (con hoja "Resumen")</p>
                                </div>
                                <Input
                                    ref={fileRef}
                                    type="file"
                                    accept=".xls,.xlsx"
                                    onChange={handleUpload}
                                    disabled={uploading}
                                    className="max-w-xs"
                                    data-testid="input-file-excel"
                                />
                            </div>
                            {uploading && (
                                <div className="mt-4 flex items-center gap-2 text-amber-400">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-400"></div>
                                    Procesando...
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {previewData && (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <Card className="card"><CardContent className="p-4">
                                    <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1"><Calendar className="w-3 h-3" /> PERIODO</div>
                                    <p className="text-sm font-semibold text-zinc-100">{previewData.fecha_inicio} a {previewData.fecha_fin}</p>
                                </CardContent></Card>
                                <Card className="card"><CardContent className="p-4">
                                    <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1"><Users className="w-3 h-3" /> EMPLEADOS</div>
                                    <p className="text-2xl font-bold text-amber-400">{previewData.vinculados.length}</p>
                                    <p className="text-xs text-zinc-500">{previewData.no_vinculados.length} no vinculados</p>
                                </CardContent></Card>
                                <Card className="card"><CardContent className="p-4">
                                    <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1"><TrendingDown className="w-3 h-3" /> DESCUENTOS</div>
                                    <p className="text-2xl font-bold text-red-400">${totalDesc.toFixed(2)}</p>
                                </CardContent></Card>
                                <Card className="card"><CardContent className="p-4">
                                    <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1"><Award className="w-3 h-3" /> BONOS $500</div>
                                    <p className="text-2xl font-bold text-emerald-400">${totalBonos.toFixed(2)}</p>
                                </CardContent></Card>
                            </div>

                            {previewData.no_vinculados.length > 0 && (
                                <Card className="card border-yellow-500/40 bg-yellow-500/5">
                                    <CardContent className="p-4">
                                        <p className="font-semibold text-yellow-400 flex items-center gap-2 mb-2">
                                            <AlertCircle className="w-5 h-5" />
                                            Empleados no vinculados ({previewData.no_vinculados.length})
                                        </p>
                                        <p className="text-xs text-zinc-400 mb-2">Estos nombres del Excel no coinciden con ningun empleado activo. Edita el campo "Nombre en Reloj Biometrico" del empleado correspondiente.</p>
                                        <div className="flex flex-wrap gap-2">
                                            {previewData.no_vinculados.map((nv, i) => (
                                                <span key={i} className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 text-xs" data-testid="no-vinculado">
                                                    {nv.nombre_excel}
                                                </span>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            <Card className="card">
                                <CardContent className="p-0">
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader><TableRow className="bg-zinc-800">
                                                <TableHead className="table-header">Empleado</TableHead>
                                                <TableHead className="table-header text-center">Hrs Req/Act</TableHead>
                                                <TableHead className="table-header text-center">Retardos</TableHead>
                                                <TableHead className="table-header text-center">Salida Temp.</TableHead>
                                                <TableHead className="table-header text-center">Faltas</TableHead>
                                                <TableHead className="table-header text-right">Sueldo Base</TableHead>
                                                <TableHead className="table-header text-right">Descuentos</TableHead>
                                                <TableHead className="table-header text-center">Bono</TableHead>
                                                <TableHead className="table-header text-right">Pago Neto</TableHead>
                                            </TableRow></TableHeader>
                                            <TableBody>
                                                {previewData.vinculados.map((r, idx) => (
                                                    <TableRow key={idx} className="table-row" data-testid="row-nomina">
                                                        <TableCell className="table-cell font-medium">{r.empleado_nombre}</TableCell>
                                                        <TableCell className="table-cell text-center text-xs">
                                                            {r.horas_requeridas} / <span className="text-amber-400">{r.horas_actuales}</span>
                                                        </TableCell>
                                                        <TableCell className="table-cell text-center text-xs">
                                                            {r.tarde_turnos > 0 ? <span className="text-red-400">{r.tarde_turnos} ({r.tarde_min}m)</span> : '-'}
                                                        </TableCell>
                                                        <TableCell className="table-cell text-center text-xs">
                                                            {r.salida_temp_turnos > 0 ? <span className="text-red-400">{r.salida_temp_turnos} ({r.salida_temp_minutos}m)</span> : '-'}
                                                        </TableCell>
                                                        <TableCell className="table-cell text-center text-xs">
                                                            {r.ausencias_total > 0
                                                                ? <span className="text-red-400">{r.ausencias_total}{r.ausencias_justificadas > 0 ? ` (${r.ausencias_justificadas}j)` : ''}</span>
                                                                : '-'}
                                                        </TableCell>
                                                        <TableCell className="table-cell text-right">${r.pago_base.toFixed(2)}</TableCell>
                                                        <TableCell className="table-cell text-right text-red-400">-${r.desc_total.toFixed(2)}</TableCell>
                                                        <TableCell className="table-cell text-center">
                                                            {r.elegible_bono
                                                                ? <span className="badge-success">+$500</span>
                                                                : <span className="text-zinc-600 text-xs">-</span>}
                                                        </TableCell>
                                                        <TableCell className="table-cell text-right font-bold text-emerald-400">${r.pago_neto.toFixed(2)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="flex flex-col sm:flex-row justify-end items-center gap-3 p-4 bg-zinc-800/40 rounded-lg">
                                <div className="text-right">
                                    <p className="text-xs text-zinc-400">TOTAL A PAGAR</p>
                                    <p className="text-3xl font-bold text-emerald-400" data-testid="total-pagar">${totalPagar.toFixed(2)}</p>
                                </div>
                                <Button onClick={() => setPreviewData(null)} variant="outline">Cancelar</Button>
                                <Button onClick={handleAprobar} className="bg-emerald-500 hover:bg-emerald-600" data-testid="btn-aprobar-nomina">
                                    <CheckCircle2 className="w-4 h-4 mr-2" /> Aprobar y Guardar
                                </Button>
                            </div>
                        </>
                    )}
                </TabsContent>

                {/* PERIODOS */}
                <TabsContent value="periodos" className="mt-4">
                    <Card className="card">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader><TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Periodo</TableHead>
                                    <TableHead className="table-header text-center">Empleados</TableHead>
                                    <TableHead className="table-header text-right">Descuentos</TableHead>
                                    <TableHead className="table-header text-right">Bonos</TableHead>
                                    <TableHead className="table-header text-right">Total</TableHead>
                                    <TableHead className="table-header">Aprobado por</TableHead>
                                    <TableHead className="table-header text-center">Acciones</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {periodos.length > 0 ? periodos.map(p => (
                                        <TableRow key={p.id} className="table-row" data-testid="row-periodo">
                                            <TableCell className="table-cell">{p.fecha_inicio} → {p.fecha_fin}</TableCell>
                                            <TableCell className="table-cell text-center">{p.num_empleados}</TableCell>
                                            <TableCell className="table-cell text-right text-red-400">-${p.total_descuentos.toFixed(2)}</TableCell>
                                            <TableCell className="table-cell text-right text-emerald-400">+${p.total_bonos.toFixed(2)}</TableCell>
                                            <TableCell className="table-cell text-right font-bold text-amber-400">${p.total_pagar.toFixed(2)}</TableCell>
                                            <TableCell className="table-cell text-zinc-400 text-xs">{p.aprobado_por}<br /><span className="text-zinc-600">{p.aprobado_at?.split('T')[0]}</span></TableCell>
                                            <TableCell className="table-cell text-center">
                                                <Button size="sm" variant="outline" onClick={() => verPeriodoDetalle(p.id)}>Ver</Button>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-400">Sin periodos guardados</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* VACACIONES */}
                <TabsContent value="vacaciones" className="space-y-4 mt-4">
                    <div className="flex justify-end">
                        <Button onClick={() => setVacOpen(true)} className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="btn-agregar-vacacion">
                            <Plane className="w-4 h-4 mr-2" /> Agregar Vacacion
                        </Button>
                    </div>
                    <Card className="card">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader><TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Empleado</TableHead>
                                    <TableHead className="table-header">Fecha Inicio</TableHead>
                                    <TableHead className="table-header">Fecha Fin</TableHead>
                                    <TableHead className="table-header">Motivo</TableHead>
                                    <TableHead className="table-header text-center">Acciones</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {vacaciones.length > 0 ? vacaciones.map(v => (
                                        <TableRow key={v.id} className="table-row" data-testid="row-vacacion">
                                            <TableCell className="table-cell font-medium">{v.empleado_nombre}</TableCell>
                                            <TableCell className="table-cell">{v.fecha_inicio}</TableCell>
                                            <TableCell className="table-cell">{v.fecha_fin}</TableCell>
                                            <TableCell className="table-cell text-zinc-400">{v.motivo || '-'}</TableCell>
                                            <TableCell className="table-cell text-center">
                                                <Button size="sm" variant="ghost" onClick={() => handleVacDelete(v.id)} className="text-red-400 hover:text-red-300">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow><TableCell colSpan={5} className="text-center py-8 text-zinc-400">Sin vacaciones registradas</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* JUSTIFICACIONES */}
                <TabsContent value="justificaciones" className="space-y-4 mt-4">
                    <div className="flex justify-end">
                        <Button onClick={() => setJustOpen(true)} className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="btn-agregar-justificacion">
                            <FileCheck className="w-4 h-4 mr-2" /> Agregar Justificacion
                        </Button>
                    </div>
                    <Card className="card">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader><TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Empleado</TableHead>
                                    <TableHead className="table-header">Fecha</TableHead>
                                    <TableHead className="table-header">Tipo</TableHead>
                                    <TableHead className="table-header">Motivo</TableHead>
                                    <TableHead className="table-header text-center">Acciones</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {justificaciones.length > 0 ? justificaciones.map(j => (
                                        <TableRow key={j.id} className="table-row" data-testid="row-justificacion">
                                            <TableCell className="table-cell font-medium">{j.empleado_nombre}</TableCell>
                                            <TableCell className="table-cell">{j.fecha}</TableCell>
                                            <TableCell className="table-cell">
                                                <span className={j.tipo === 'falta' ? 'badge-danger' : 'badge-warning'}>{j.tipo}</span>
                                            </TableCell>
                                            <TableCell className="table-cell text-zinc-400">{j.motivo}</TableCell>
                                            <TableCell className="table-cell text-center">
                                                <Button size="sm" variant="ghost" onClick={() => handleJustDelete(j.id)} className="text-red-400 hover:text-red-300">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow><TableCell colSpan={5} className="text-center py-8 text-zinc-400">Sin justificaciones registradas</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Modal Vacacion */}
            <Dialog open={vacOpen} onOpenChange={setVacOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="modal-vacacion">
                    <DialogHeader><DialogTitle>Nueva Vacacion</DialogTitle></DialogHeader>
                    <form onSubmit={handleVacSubmit} className="space-y-4">
                        <div className="form-group"><Label>Empleado *</Label>
                            <Select value={vacForm.empleado_id} onValueChange={v => setVacForm({ ...vacForm, empleado_id: v })}>
                                <SelectTrigger data-testid="select-emp-vacacion"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                <SelectContent>{empleados.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label>Inicio *</Label>
                                <Input type="date" value={vacForm.fecha_inicio} onChange={e => setVacForm({ ...vacForm, fecha_inicio: e.target.value })} data-testid="input-vac-inicio" />
                            </div>
                            <div className="form-group"><Label>Fin *</Label>
                                <Input type="date" value={vacForm.fecha_fin} onChange={e => setVacForm({ ...vacForm, fecha_fin: e.target.value })} data-testid="input-vac-fin" />
                            </div>
                        </div>
                        <div className="form-group"><Label>Motivo</Label>
                            <Input value={vacForm.motivo} onChange={e => setVacForm({ ...vacForm, motivo: e.target.value })} placeholder="Opcional" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setVacOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="submit-vacacion">Guardar</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modal Justificacion */}
            <Dialog open={justOpen} onOpenChange={(o) => { setJustOpen(o); if (!o) { setJustGlobal(false); setJustExcepciones([]); } }}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="modal-justificacion">
                    <DialogHeader><DialogTitle>Nueva Justificacion</DialogTitle></DialogHeader>
                    <form onSubmit={handleJustSubmit} className="space-y-4">
                        {/* Toggle global */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-amber-400/10 border border-amber-400/30">
                            <div>
                                <p className="text-sm font-medium text-amber-300">Aplicar a todos los empleados (global)</p>
                                <p className="text-[10px] text-zinc-400 mt-0.5">Crea una justificacion por cada empleado activo. Util para dias festivos no oficiales o cierres.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setJustGlobal(!justGlobal)}
                                className={`w-12 h-6 rounded-full transition relative ${justGlobal ? 'bg-amber-400' : 'bg-zinc-700'}`}
                                data-testid="toggle-just-global"
                            >
                                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${justGlobal ? 'left-6' : 'left-0.5'}`} />
                            </button>
                        </div>

                        {!justGlobal ? (
                            <div className="form-group"><Label>Empleado *</Label>
                                <Select value={justForm.empleado_id} onValueChange={v => setJustForm({ ...justForm, empleado_id: v })}>
                                    <SelectTrigger data-testid="select-emp-just"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{empleados.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div className="form-group">
                                <div className="flex items-center justify-between">
                                    <Label>Excepciones (empleados a NO justificar)</Label>
                                    <span className="text-[10px] text-emerald-400">
                                        {empleados.length - justExcepciones.length} de {empleados.length} se justificaran
                                    </span>
                                </div>
                                <div className="max-h-48 overflow-y-auto rounded-lg border border-zinc-700 p-2 grid grid-cols-2 gap-1 mt-1" data-testid="lista-excepciones">
                                    {empleados.map(emp => {
                                        const excluido = justExcepciones.includes(emp.id);
                                        return (
                                            <button
                                                key={emp.id}
                                                type="button"
                                                onClick={() => setJustExcepciones(prev =>
                                                    excluido ? prev.filter(id => id !== emp.id) : [...prev, emp.id]
                                                )}
                                                className={`text-left text-xs px-2 py-1 rounded transition ${excluido ? 'bg-red-500/20 text-red-300 line-through' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200'}`}
                                                data-testid={`excepcion-${emp.id}`}
                                            >
                                                {excluido ? '✗ ' : '✓ '}{emp.nombre}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-[10px] text-zinc-500 mt-1">Click en un empleado para excluirlo del registro masivo (no se le creara justificacion).</p>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label>Fecha *</Label>
                                <Input type="date" value={justForm.fecha} onChange={e => setJustForm({ ...justForm, fecha: e.target.value })} data-testid="input-just-fecha" />
                            </div>
                            <div className="form-group"><Label>Tipo *</Label>
                                <Select value={justForm.tipo} onValueChange={v => setJustForm({ ...justForm, tipo: v })}>
                                    <SelectTrigger data-testid="select-just-tipo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="falta">Falta</SelectItem>
                                        <SelectItem value="retardo">Retardo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="form-group"><Label>Motivo *</Label>
                            <Input value={justForm.motivo} onChange={e => setJustForm({ ...justForm, motivo: e.target.value })} placeholder="Ej: Cita medica, incapacidad..." />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setJustOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="submit-justificacion">
                                {justGlobal ? `Guardar (${empleados.length - justExcepciones.length})` : 'Guardar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modal Detalle Periodo */}
            <Dialog open={detalleOpen} onOpenChange={setDetalleOpen}>
                <DialogContent className="sm:max-w-[1100px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            Periodo {periodoDetalle?.fecha_inicio} → {periodoDetalle?.fecha_fin}
                        </DialogTitle>
                    </DialogHeader>
                    {periodoDetalle && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <Card className="card"><CardContent className="p-3">
                                    <p className="text-xs text-zinc-400">Descuentos</p>
                                    <p className="text-xl font-bold text-red-400">-${periodoDetalle.total_descuentos.toFixed(2)}</p>
                                </CardContent></Card>
                                <Card className="card"><CardContent className="p-3">
                                    <p className="text-xs text-zinc-400">Bonos</p>
                                    <p className="text-xl font-bold text-emerald-400">+${periodoDetalle.total_bonos.toFixed(2)}</p>
                                </CardContent></Card>
                                <Card className="card"><CardContent className="p-3">
                                    <p className="text-xs text-zinc-400">Total Pagado</p>
                                    <p className="text-xl font-bold text-amber-400">${periodoDetalle.total_pagar.toFixed(2)}</p>
                                </CardContent></Card>
                            </div>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader><TableRow className="bg-zinc-800">
                                        <TableHead className="table-header">Empleado</TableHead>
                                        <TableHead className="table-header text-center">Retardos</TableHead>
                                        <TableHead className="table-header text-center">Faltas</TableHead>
                                        <TableHead className="table-header text-right">Sueldo Base</TableHead>
                                        <TableHead className="table-header text-right">Descuentos</TableHead>
                                        <TableHead className="table-header text-center">Bono</TableHead>
                                        <TableHead className="table-header text-right">Pago Neto</TableHead>
                                        <TableHead className="table-header text-center">Recibo</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {periodoDetalle.registros?.map((r, idx) => (
                                            <TableRow key={idx} className="table-row">
                                                <TableCell className="table-cell font-medium">{r.empleado_nombre}</TableCell>
                                                <TableCell className="table-cell text-center text-xs">{r.tarde_turnos > 0 ? `${r.tarde_turnos} (${r.tarde_min}m)` : '-'}</TableCell>
                                                <TableCell className="table-cell text-center text-xs">{r.ausencias_total > 0 ? r.ausencias_total : '-'}</TableCell>
                                                <TableCell className="table-cell text-right">${(r.pago_base || 0).toFixed(2)}</TableCell>
                                                <TableCell className="table-cell text-right text-red-400">-${(r.desc_total || 0).toFixed(2)}</TableCell>
                                                <TableCell className="table-cell text-center">{r.elegible_bono ? <span className="badge-success">+$500</span> : '-'}</TableCell>
                                                <TableCell className="table-cell text-right font-bold text-emerald-400">${(r.pago_neto || 0).toFixed(2)}</TableCell>
                                                <TableCell className="table-cell text-center">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => descargarRecibo(periodoDetalle.id, r.empleado_id, r.empleado_nombre)}
                                                        className="h-8 px-2"
                                                        data-testid={`btn-recibo-${r.empleado_id}`}
                                                    >
                                                        <Download className="w-3.5 h-3.5" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
